export type BottomNavbarPaddingMode = boolean | number;

export interface GetViewBottomPaddingParams {
  bottomNavbarPadding?: BottomNavbarPaddingMode;
  isNavbarVisible: boolean;
  isNative: boolean;
  bottomNavbarHeight: string;
}

/**
 * Computes the bottom padding a `View` needs so its content clears the fixed
 * bottom navbar (when one is actually rendered) and, on native platforms, the
 * device's safe-area inset.
 *
 * - `undefined` (default): pad for the navbar only if it is currently visible.
 * - `true`: force one navbar height of padding (back-compat opt-in),
 *   regardless of visibility.
 * - `false`: never pad for the navbar, regardless of visibility.
 * - `number`: pad for the navbar (if visible) plus this many extra pixels,
 *   e.g. the measured height of a screen-owned bar rendered above the navbar
 *   (the site-unit order action bar). Passing the actual measured height
 *   avoids assuming that bar is exactly one navbar height tall.
 */
export const getViewBottomPadding = ({
  bottomNavbarPadding,
  isNavbarVisible,
  isNative,
  bottomNavbarHeight,
}: GetViewBottomPaddingParams): string | undefined => {
  const parts = getPaddingParts(
    bottomNavbarPadding,
    isNavbarVisible,
    bottomNavbarHeight,
  );

  if (parts.length === 0) {
    return isNative ? 'var(--safe-area-inset-bottom)' : undefined;
  }

  const total = parts.length === 1 ? parts[0] : `calc(${parts.join(' + ')})`;

  return isNative ? `calc(var(--safe-area-inset-bottom) + ${total})` : total;
};

const getPaddingParts = (
  bottomNavbarPadding: BottomNavbarPaddingMode | undefined,
  isNavbarVisible: boolean,
  bottomNavbarHeight: string,
): string[] => {
  if (bottomNavbarPadding === false) {
    return [];
  }

  if (bottomNavbarPadding === true) {
    return [bottomNavbarHeight];
  }

  if (typeof bottomNavbarPadding === 'number') {
    const parts: string[] = [];

    if (isNavbarVisible) {
      parts.push(bottomNavbarHeight);
    }

    if (bottomNavbarPadding > 0) {
      parts.push(`${bottomNavbarPadding}px`);
    }

    return parts;
  }

  return isNavbarVisible ? [bottomNavbarHeight] : [];
};
